import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

/**
 * Unit option for dropdown
 */
export interface UnitOption {
  value: string;
  label: string;
  shortLabel?: string; // For compact display
}

/**
 * Value emitted by component
 */
export interface UnitValue {
  value: number | null;
  unit: string;
  formatted: string; // e.g., "10 ft" for storage
}

/**
 * Reusable Unit Input Component
 *
 * Combines a number input with a unit dropdown for standardized measurements.
 * Implements ControlValueAccessor for form integration.
 *
 * Example usage:
 * <app-unit-input
 *   [units]="distanceUnits"
 *   [defaultUnit]="'ft'"
 *   [placeholder]="'10'"
 *   formControlName="nozzleDistance">
 * </app-unit-input>
 */
@Component({
  selector: 'app-unit-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './unit-input.component.html',
  styleUrls: ['./unit-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UnitInputComponent),
      multi: true
    }
  ]
})
export class UnitInputComponent implements ControlValueAccessor {
  @Input() units: UnitOption[] = [];
  @Input() defaultUnit: string = '';
  @Input() placeholder: string = '';
  @Input() min?: number;
  @Input() max?: number;
  @Input() step: number = 1;
  @Input() disabled: boolean = false;

  @Output() valueChange = new EventEmitter<UnitValue | null>();

  // Internal state
  numericValue: number | null = null;
  selectedUnit: string = '';

  // ControlValueAccessor callbacks
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    // Set default unit if not already set
    if (!this.selectedUnit && this.defaultUnit) {
      this.selectedUnit = this.defaultUnit;
    } else if (!this.selectedUnit && this.units.length > 0) {
      this.selectedUnit = this.units[0].value;
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string | null): void {
    if (!value) {
      this.numericValue = null;
      this.selectedUnit = this.defaultUnit || (this.units.length > 0 ? this.units[0].value : '');
      return;
    }

    // Parse "10 ft" format
    const parsed = this.parseValue(value);
    if (parsed) {
      this.numericValue = parsed.value;
      this.selectedUnit = parsed.unit;
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Handle numeric value change
  onValueChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (!value || value.trim() === '') {
      this.numericValue = null;
      this.emitChange();
      return;
    }

    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      this.numericValue = parsed;
      this.emitChange();
    }
  }

  // Handle unit change
  onUnitChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.selectedUnit = select.value;
    this.emitChange();
  }

  // Handle blur
  onBlur(): void {
    this.onTouched();
  }

  // Emit change to form control
  private emitChange(): void {
    if (this.numericValue === null) {
      this.onChange(null);
      this.valueChange.emit(null);
      return;
    }

    const formatted = `${this.numericValue} ${this.selectedUnit}`;
    const unitValue: UnitValue = {
      value: this.numericValue,
      unit: this.selectedUnit,
      formatted
    };

    this.onChange(formatted);
    this.valueChange.emit(unitValue);
  }

  // Parse "10 ft" format
  private parseValue(value: string): { value: number; unit: string } | null {
    const parts = value.trim().split(/\s+/);
    if (parts.length === 2) {
      const numValue = parseFloat(parts[0]);
      if (!isNaN(numValue)) {
        return { value: numValue, unit: parts[1] };
      }
    }

    // Try parsing just number (use default unit)
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      return { value: numValue, unit: this.selectedUnit || this.defaultUnit };
    }

    return null;
  }

  // Get short label for unit
  getUnitShortLabel(unitValue: string): string {
    const unit = this.units.find(u => u.value === unitValue);
    return unit?.shortLabel || unit?.label || unitValue;
  }
}
